import { useState } from 'react';
import type { Customer, MeterSearch } from '@metron/shared';
import { METER_SEARCH_BRANDS, meterSearchSchema } from '@metron/shared';
import { Button } from '../ui/button.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { CustomerPicker } from '../CustomerPicker.tsx';
import { type FilterChipItem, FilterChipList } from '../search/FilterChipList.tsx';

/** Spec fields a user can add or edit here; paging and sort belong to the table. */
type FilterField =
  | 'customerId'
  | 'propertyId'
  | 'sensorGroupId'
  | 'text'
  | 'lastContactOlderThanDays'
  | 'lastContactWithinDays'
  | 'rateMin'
  | 'rateMax'
  | 'leakRateMin'
  | 'backflowRateMin'
  | ChoiceField;

/** Single-choice fields, edited with selects (MET-3560). */
type ChoiceField = 'product' | 'meterBrand' | 'source' | 'sizeClass';

const PRODUCT_LABELS: Record<NonNullable<MeterSearch['product']>, string> = {
  wave: 'Wave (Astria)',
  vn: 'VN (incl. VN Remote)',
  vnRemote: 'VN Remote (VNR)',
  fsk: 'FSK',
  legacy: 'Legacy register',
};
const SOURCE_LABELS: Record<NonNullable<MeterSearch['source']>, string> = {
  metron: 'Metron',
  waterSignal: 'WaterSignal',
  external: 'External (DataLink)',
};
const SIZE_LABELS: Record<NonNullable<MeterSearch['sizeClass']>, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
  hydrant: 'Hydrant',
};
const BRAND_LABELS = Object.fromEntries(METER_SEARCH_BRANDS.map((b) => [b, b])) as Record<
  NonNullable<MeterSearch['meterBrand']>,
  string
>;

const CHOICES: Array<{ field: ChoiceField; label: string; options: Record<string, string> }> = [
  { field: 'product', label: 'Product', options: PRODUCT_LABELS },
  { field: 'meterBrand', label: 'Brand', options: BRAND_LABELS },
  { field: 'source', label: 'Source', options: SOURCE_LABELS },
  { field: 'sizeClass', label: 'Size', options: SIZE_LABELS },
];

// Radix Select items can't have an empty value, so "no filter" is a sentinel.
const ANY = 'any';

export interface FilterChip extends FilterChipItem<FilterField> {
  /** Fields cleared when the chip is removed (rate min/max share one chip). */
  fields: FilterField[];
}

const gpm = (n: number) => `${n} GPM`;

/**
 * One chip per active filter, in a fixed order. Internal ids never appear in a
 * label: the customer shows by name, property and sensor group generically.
 */
export function describeFilters(spec: MeterSearch, customerName?: string): FilterChip[] {
  const chips: FilterChip[] = [];
  if (spec.customerId !== undefined) {
    chips.push({
      id: 'customerId',
      label: `Customer: ${customerName ?? 'selected customer'}`,
      fields: ['customerId'],
      editable: true,
    });
  }
  // ponytail: property and group show generically; add name lookups when a
  // lightweight by-id endpoint exists (today it would mean listing everything).
  if (spec.propertyId !== undefined) {
    chips.push({
      id: 'propertyId',
      label: 'Property filter',
      fields: ['propertyId'],
      editable: false,
    });
  }
  if (spec.sensorGroupId !== undefined) {
    chips.push({
      id: 'sensorGroupId',
      label: 'Sensor group filter',
      fields: ['sensorGroupId'],
      editable: false,
    });
  }
  if (spec.text !== undefined) {
    chips.push({ id: 'text', label: `Matches “${spec.text}”`, fields: ['text'], editable: true });
  }
  if (spec.lastContactOlderThanDays !== undefined) {
    const d = spec.lastContactOlderThanDays;
    chips.push({
      id: 'lastContactOlderThanDays',
      label: `No contact in ${d} day${d === 1 ? '' : 's'}`,
      fields: ['lastContactOlderThanDays'],
      editable: true,
    });
  }
  if (spec.lastContactWithinDays !== undefined) {
    const d = spec.lastContactWithinDays;
    chips.push({
      id: 'lastContactWithinDays',
      label: `Contacted in last ${d} day${d === 1 ? '' : 's'}`,
      fields: ['lastContactWithinDays'],
      editable: true,
    });
  }
  const { rateMin, rateMax } = spec;
  const rateLabel = rateMin !== undefined && rateMax !== undefined
    ? `Rate ${rateMin}–${gpm(rateMax)}`
    : rateMin !== undefined
    ? `Rate ≥ ${gpm(rateMin)}`
    : rateMax !== undefined
    ? `Rate ≤ ${gpm(rateMax)}`
    : null;
  if (rateLabel) {
    chips.push({ id: 'rateMin', label: rateLabel, fields: ['rateMin', 'rateMax'], editable: true });
  }
  if (spec.leakRateMin !== undefined) {
    chips.push({
      id: 'leakRateMin',
      label: `Leak ≥ ${gpm(spec.leakRateMin)}`,
      fields: ['leakRateMin'],
      editable: true,
    });
  }
  if (spec.backflowRateMin !== undefined) {
    chips.push({
      id: 'backflowRateMin',
      label: `Backflow ≥ ${gpm(spec.backflowRateMin)}`,
      fields: ['backflowRateMin'],
      editable: true,
    });
  }
  for (const { field, label, options } of CHOICES) {
    const value = spec[field];
    if (value !== undefined) {
      chips.push({
        id: field,
        label: `${label}: ${options[value]}`,
        fields: [field],
        editable: true,
      });
    }
  }
  return chips;
}

/** Drop the given filter fields and return to page 1. */
export function withoutFields(spec: MeterSearch, fields: FilterField[]): MeterSearch {
  const next: MeterSearch = { ...spec, page: 1 };
  for (const f of fields) delete next[f];
  return next;
}

// Editor fields are strings while typing; empty means "no filter".
type Draft = Record<
  Exclude<FilterField, 'customerId' | 'propertyId' | 'sensorGroupId' | ChoiceField>,
  string
>;

const NUMBER_FIELDS: Array<{ field: keyof Draft; label: string }> = [
  { field: 'lastContactOlderThanDays', label: 'No contact in (days)' },
  { field: 'lastContactWithinDays', label: 'Contacted within (days)' },
  { field: 'rateMin', label: 'Rate at least (GPM)' },
  { field: 'rateMax', label: 'Rate at most (GPM)' },
  { field: 'leakRateMin', label: 'Leak at least (GPM)' },
  { field: 'backflowRateMin', label: 'Backflow at least (GPM)' },
];

function toDraft(spec: MeterSearch): Draft {
  const s = (v: number | string | undefined) => (v === undefined ? '' : String(v));
  return {
    text: s(spec.text),
    lastContactOlderThanDays: s(spec.lastContactOlderThanDays),
    lastContactWithinDays: s(spec.lastContactWithinDays),
    rateMin: s(spec.rateMin),
    rateMax: s(spec.rateMax),
    leakRateMin: s(spec.leakRateMin),
    backflowRateMin: s(spec.backflowRateMin),
  };
}

interface FilterEditorProps {
  spec: MeterSearch;
  customers: Customer[];
  customersLoading: boolean;
  focusField: FilterField | null;
  onApply: (next: MeterSearch) => void;
}

function FilterEditor(
  { spec, customers, customersLoading, focusField, onApply }: FilterEditorProps,
) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(spec));
  const [customerId, setCustomerId] = useState<number | undefined>(spec.customerId);
  const [choices, setChoices] = useState<Record<ChoiceField, string>>(() => ({
    product: spec.product ?? ANY,
    meterBrand: spec.meterBrand ?? ANY,
    source: spec.source ?? ANY,
    sizeClass: spec.sizeClass ?? ANY,
  }));
  const [errors, setErrors] = useState<string[]>([]);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const candidate: Record<string, unknown> = { ...spec, page: 1, customerId };
    candidate.text = draft.text.trim() === '' ? undefined : draft.text;
    for (const { field } of NUMBER_FIELDS) {
      candidate[field] = draft[field].trim() === '' ? undefined : Number(draft[field]);
    }
    for (const { field } of CHOICES) {
      candidate[field] = choices[field] === ANY ? undefined : choices[field];
    }
    const parsed = meterSearchSchema.safeParse(
      Object.fromEntries(Object.entries(candidate).filter(([, v]) => v !== undefined)),
    );
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onApply(parsed.data);
  };

  return (
    <form onSubmit={apply} className='space-y-3' aria-label='Edit filters'>
      <div className='space-y-1'>
        <Label htmlFor='meter-search-customer'>Customer</Label>
        <CustomerPicker
          inputId='meter-search-customer'
          customers={customers}
          isLoading={customersLoading}
          selectedCustomerId={customerId}
          onSelect={setCustomerId}
          onClear={() => setCustomerId(undefined)}
        />
      </div>
      <div className='space-y-1'>
        <Label htmlFor='meter-search-text'>Device ID, address, or consumer</Label>
        <Input
          id='meter-search-text'
          value={draft.text}
          autoFocus={focusField === 'text'}
          onChange={(e) => setDraft({ ...draft, text: e.target.value })}
        />
      </div>
      <div className='grid grid-cols-2 gap-3'>
        {NUMBER_FIELDS.map(({ field, label }) => (
          <div key={field} className='space-y-1'>
            <Label htmlFor={`meter-search-${field}`}>{label}</Label>
            <Input
              id={`meter-search-${field}`}
              type='number'
              inputMode='decimal'
              min={0}
              step='any'
              value={draft[field]}
              autoFocus={focusField === field}
              onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
            />
          </div>
        ))}
      </div>
      <div className='grid grid-cols-2 gap-3'>
        {CHOICES.map(({ field, label, options }) => (
          <div key={field} className='space-y-1'>
            <Label htmlFor={`meter-search-${field}`}>{label}</Label>
            <Select
              name={field}
              value={choices[field]}
              onValueChange={(value) => setChoices({ ...choices, [field]: value })}
            >
              <SelectTrigger id={`meter-search-${field}`} autoFocus={focusField === field}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any</SelectItem>
                {Object.entries(options).map(([value, text]) => (
                  <SelectItem key={value} value={value}>{text}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      {errors.length > 0 && (
        <ul role='alert' className='text-sm text-destructive list-disc pl-5'>
          {/* Replaced wholesale on each submit, and two issues can share a message. */}
          {errors.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      <div className='flex justify-end'>
        <Button type='submit' size='sm'>Apply</Button>
      </div>
    </form>
  );
}

interface MeterSearchFiltersProps {
  spec: MeterSearch;
  customers: Customer[];
  customersLoading: boolean;
  onChange: (next: MeterSearch) => void;
}

/** Active filters as chips (click to edit, × to remove) plus an Add filter editor. */
export function MeterSearchFilters({
  spec,
  customers,
  customersLoading,
  onChange,
}: MeterSearchFiltersProps) {
  const customerName = customers.find((c) => c.id === spec.customerId)?.waterscopeAccountName;

  return (
    <FilterChipList
      chips={describeFilters(spec, customerName)}
      emptyText='No filters: showing all installed meters.'
      onRemove={(chip) => onChange(withoutFields(spec, chip.fields))}
      renderEditor={(focusField, close) => (
        <FilterEditor
          spec={spec}
          customers={customers}
          customersLoading={customersLoading}
          focusField={focusField}
          onApply={(next) => {
            close();
            onChange(next);
          }}
        />
      )}
    />
  );
}
